import { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/utils/cn';

/**
 * Semantic badge variants, mapped 1:1 to the pill colors already
 * hand-rolled across the admin pages (Users.tsx status pill, Teams.tsx
 * statusColors map, "Portfolio Staff"/"Super"/"Managed Brand" pills).
 */
export type BadgeVariant = 'success' | 'warning' | 'danger' | 'neutral' | 'info' | 'primary';

const VARIANT_CLASSES: Record<BadgeVariant, string> = {
  // Users.isActive / Teams.ACTIVE
  success: 'bg-green-100 text-green-700',
  // Teams.GRACE_PERIOD
  warning: 'bg-yellow-100 text-yellow-700',
  // Users.isActive=false / Teams.EXPIRED
  danger: 'bg-red-100 text-red-700',
  // Teams.CANCELLED / UserDetail "Not super admin" / "Full Access"
  neutral: 'bg-gray-100 text-gray-700',
  // UserDetail "Current Role" pill / "Read-Only" impersonation mode
  info: 'bg-blue-100 text-blue-700',
  // "Portfolio Staff" / "Super" / "Managed Brand" pills
  primary: 'bg-primary-50 text-primary-800',
};

export interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'className'> {
  /** Badge label content */
  children: ReactNode;
  /** Semantic color variant (default 'neutral') */
  variant?: BadgeVariant;
  /** Additional classes appended to the base styles. Note: not guaranteed to override conflicting base utilities (cn is a concat, not tailwind-merge) — prefer additive classes. */
  className?: string;
}

/**
 * Badge - Semantic status pill matching the inline pills already used
 * across admin tables (status, role, "Portfolio Staff", etc.).
 *
 * @example
 * <Badge variant={user.isActive ? 'success' : 'danger'}>
 *   {user.isActive ? 'Active' : 'Inactive'}
 * </Badge>
 *
 * @example
 * <Badge variant="primary">Portfolio Staff</Badge>
 */
export const Badge = ({
  children,
  variant = 'neutral',
  className = '',
  ...props
}: BadgeProps) => {
  return (
    <span
      className={cn(
        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
        VARIANT_CLASSES[variant],
        className
      )}
      {...props}
    >
      {children}
    </span>
  );
};

export default Badge;
